import React, { useCallback, useEffect, useState } from "react";
import { CPDropdown } from "../../CPDropdown";
import { useConfigurationCatalogs } from "../../../hooks/useConfigurationCatalogs";
import {
  isAssignableManagerUser,
  listAllUsers,
  saveAdminUser,
} from "../../../helpers/admin/usersAdmin";
import {
  listActiveTrades,
  tradesToDropdownOptions,
} from "../../../helpers/catalogs/tradesCatalog";
import { usePermissions } from "../../../hooks/usePermissions";
import { buildTradeAssignmentFields } from "../../../helpers/tradeAssignment";
import { AdminFormCard } from "../shared/AdminFormCard";
import { AdminLegacyHeader, AdminListRow } from "../shared/AdminFormCard";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { DeskEmpty, DeskPill, DeskSearch, DeskTable, DeskToolbar } from "../../desktop/DeskTable";
import "../adminDesktop.css";

function userRoleLabel(user) {
  return user.membership_role === "owner" ? "owner" : user.role || "employee";
}

export function AdminUsersDirectoryPanel({
  onBack,
  showLegacyHeader = false,
  onSelectUser,
  currentUser,
}) {
  const isDesktop = useIsDesktopViewport();
  const [query, setQuery] = useState("");
  const [allUsers, setAllUsers] = useState([]);

  const loadAllUsers = useCallback(async () => {
    if (!currentUser) {
      return;
    }
    try {
      const data = await listAllUsers(currentUser);
      setAllUsers(data);
    } catch (err) {
      console.error("LOAD USERS ERROR:", err);
    }
  }, [currentUser]);

  useEffect(() => {
    loadAllUsers();
  }, [loadAllUsers]);

  const handleSelectUser = (user) => {
    if (onSelectUser) {
      onSelectUser(user);
      return;
    }
  };

  const normalizedQuery = query.trim().toLowerCase();
  const shownUsers = normalizedQuery
    ? allUsers.filter((user) =>
        [user.employee_name, user.email, user.classification_name]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedQuery)),
      )
    : allUsers;

  const desktopContent = (
    <>
      <DeskToolbar>
        <DeskSearch
          value={query}
          onChange={setQuery}
          placeholder="Search users"
          ariaLabel="Search users"
        />
        <span className="dt-toolbar__grow" />
        <span className="dt-summary">
          {shownUsers.length} user{shownUsers.length === 1 ? "" : "s"}
        </span>
      </DeskToolbar>
      {allUsers.length === 0 ? (
        <DeskEmpty title="No users found." />
      ) : shownUsers.length === 0 ? (
        <DeskEmpty title="No users match your search" />
      ) : (
        <DeskTable
          caption="All users"
          rows={shownUsers}
          rowKey={(user) => user.supabase_user_id}
          onOpen={handleSelectUser}
          openLabel={(user) => `Open ${user.employee_name}`}
          columns={[
            {
              key: "name",
              label: "Employee",
              width: "30%",
              primary: true,
              render: (user) => (
                <span className="adm-who">
                  <span className="adm-avatar" aria-hidden="true">
                    {(user.employee_name || "?").trim().charAt(0).toUpperCase()}
                  </span>
                  <span className="dt-item__text">
                    <span className="dt-strong">{user.employee_name}</span>
                    <span className="dt-sub">{user.email || "—"}</span>
                  </span>
                </span>
              ),
            },
            {
              key: "role",
              label: "Role",
              width: "14%",
              render: (user) => (
                <DeskPill tone={user.membership_role === "owner" ? "gold" : "muted"}>
                  {userRoleLabel(user)}
                </DeskPill>
              ),
            },
            {
              key: "class",
              label: "Classification",
              render: (user) => (
                <span className="dt-muted">{user.classification_name || "—"}</span>
              ),
            },
            {
              key: "manager",
              label: "Manager",
              render: (user) => (
                <span className="dt-muted">
                  {allUsers.find((u) => u.supabase_user_id === user.manager_supabase_user_id)
                    ?.employee_name || "—"}
                </span>
              ),
            },
            {
              key: "finance",
              label: "Finance",
              width: "110px",
              render: (user) =>
                user.finance_access ? (
                  <DeskPill tone="green">Finance</DeskPill>
                ) : (
                  <span className="dt-muted">—</span>
                ),
            },
          ]}
        />
      )}
    </>
  );

  const content = isDesktop ? desktopContent : (
    <AdminFormCard title="ALL USERS">
      {allUsers.length === 0 ? (
        <p className="profile-value">No users found.</p>
      ) : (
        allUsers.map((user) => (
          <AdminListRow
            key={user.supabase_user_id}
            onClick={() => handleSelectUser(user)}
          >
            {user.employee_name} (
            {user.membership_role === "owner"
              ? "owner"
              : user.role || "employee"}
            )
          </AdminListRow>
        ))
      )}
    </AdminFormCard>
  );

  if (showLegacyHeader) {
    return (
      <div className="intel-screen">
        <AdminLegacyHeader title="USER MANAGEMENT" onBack={onBack} />
        {content}
      </div>
    );
  }

  return content;
}

export function AdminUserDetailPanel({
  user,
  allUsers,
  onBack,
  onSaved,
  showLegacyHeader = true,
  currentUser,
}) {
  const { actor } = usePermissions();
  const isDesktop = useIsDesktopViewport();
  const adminActor = currentUser || actor;
  const { classificationOptions } = useConfigurationCatalogs();
  const [trades, setTrades] = useState([]);
  const [editRole, setEditRole] = useState(user?.role || "");
  const [editManagerId, setEditManagerId] = useState(user?.manager_supabase_user_id || "");
  const [editClassification, setEditClassification] = useState(
    user?.classification_name || "",
  );
  const [editTradeId, setEditTradeId] = useState(user?.trade_id || "");
  const [editFinanceAccess, setEditFinanceAccess] = useState(
    user?.finance_access === true,
  );

  useEffect(() => {
    if (!adminActor) {
      return;
    }

    listActiveTrades(adminActor)
      .then(setTrades)
      .catch((err) => console.error("LOAD TRADES ERROR:", err));
  }, [adminActor]);

  const isOrganizationOwner = user?.membership_role === "owner";

  useEffect(() => {
    setEditRole(isOrganizationOwner ? "admin" : user?.role || "");
    setEditManagerId(user?.manager_supabase_user_id || "");
    setEditClassification(user?.classification_name || "");
    setEditTradeId(user?.trade_id || "");
    setEditFinanceAccess(user?.finance_access === true);
  }, [user, isOrganizationOwner]);

  const tradeOptions = tradesToDropdownOptions(trades);

  const handleSaveAdminUser = async () => {
    try {
      const tradeFields = buildTradeAssignmentFields(editTradeId, trades);

      const result = await saveAdminUser(adminActor, {
        supabase_user_id: user.supabase_user_id,
        role: isOrganizationOwner ? "admin" : editRole,
        manager_supabase_user_id: editManagerId,
        classification_name: editClassification,
        finance_access: editFinanceAccess,
        ...tradeFields,
      });

      if (result?.ownerProtected) {
        alert(
          "Organization Owner role is protected and was not changed. Other fields were saved.",
        );
      }

      onSaved?.({
        ...user,
        role: result?.role || editRole,
        membership_role: result?.membership_role || user.membership_role,
        manager_supabase_user_id: editManagerId,
        classification_name: editClassification,
        finance_access: editFinanceAccess,
        ...tradeFields,
      });
    } catch (err) {
      console.error("SAVE ADMIN USER ERROR:", err);
      alert(err?.message || "Failed to update user");
    }
  };

  const roleControl = isOrganizationOwner ? (
    <div className="adm-wide">
      <div className="dp-label">Role</div>
      <p className="profile-value">Organization Owner (cannot be changed here)</p>
    </div>
  ) : (
    <CPDropdown
      label="Role"
      value={editRole}
      onChange={setEditRole}
      options={[
        { value: "employee", label: "Employee" },
        { value: "manager", label: "Manager" },
        { value: "admin", label: "Admin" },
      ]}
    />
  );

  const desktopContent = (
    <div className="adm-main-side">
      <AdminFormCard title="ACCESS & ASSIGNMENT">
        <div className="adm-fields">
          {roleControl}
          <CPDropdown
            label="Manager"
            value={editManagerId}
            onChange={setEditManagerId}
            options={[
              { value: "", label: "No Manager Assigned" },
              ...(allUsers || [])
                .filter((u) => isAssignableManagerUser(u))
                .map((manager) => ({
                  value: manager.supabase_user_id,
                  label: manager.employee_name,
                })),
            ]}
          />
          <CPDropdown
            label="Classification"
            value={editClassification}
            onChange={setEditClassification}
            options={classificationOptions}
          />
          <CPDropdown
            label="Trade"
            value={editTradeId}
            onChange={setEditTradeId}
            options={tradeOptions}
          />
          <CPDropdown
            label="Finance access"
            value={editFinanceAccess ? "true" : "false"}
            onChange={(value) => setEditFinanceAccess(value === "true")}
            options={[
              { value: "false", label: "No" },
              { value: "true", label: "Yes — Finance module" },
            ]}
          />
        </div>
        <div className="adm-actions">
          <button type="button" className="kpi-back-btn adm-primary" onClick={handleSaveAdminUser}>
            Save User
          </button>
        </div>
      </AdminFormCard>

      <AdminFormCard title="EMPLOYEE">
        <dl className="adm-kv">
          <dt>Name</dt>
          <dd>{user.employee_name}</dd>
          <dt>Email</dt>
          <dd>{user.email || "—"}</dd>
        </dl>
      </AdminFormCard>
    </div>
  );

  const content = isDesktop ? desktopContent : (
    <AdminFormCard>
      <div className="section-heading">NAME</div>
      <p className="profile-value">{user.employee_name}</p>

      <div className="section-heading">EMAIL</div>
      <p className="profile-value">{user.email}</p>

      {isOrganizationOwner ? (
        <>
          <div className="section-heading">ROLE</div>
          <p className="profile-value">
            Organization Owner (cannot be changed here)
          </p>
        </>
      ) : (
        <CPDropdown
          label="Role"
          value={editRole}
          onChange={setEditRole}
          options={[
            { value: "employee", label: "Employee" },
            { value: "manager", label: "Manager" },
            { value: "admin", label: "Admin" },
          ]}
        />
      )}

      <CPDropdown
        label="Manager"
        value={editManagerId}
        onChange={setEditManagerId}
        options={[
          { value: "", label: "No Manager Assigned" },
          ...(allUsers || [])
            .filter((u) => isAssignableManagerUser(u))
            .map((manager) => ({
              value: manager.supabase_user_id,
              label: manager.employee_name,
            })),
        ]}
      />

      <CPDropdown
        label="Classification"
        value={editClassification}
        onChange={setEditClassification}
        options={classificationOptions}
      />

      <CPDropdown
        label="Trade"
        value={editTradeId}
        onChange={setEditTradeId}
        options={tradeOptions}
      />

      <CPDropdown
        label="Finance access"
        value={editFinanceAccess ? "true" : "false"}
        onChange={(value) => setEditFinanceAccess(value === "true")}
        options={[
          { value: "false", label: "No" },
          { value: "true", label: "Yes — Finance module" },
        ]}
      />

      <button type="button" className="kpi-back-btn" onClick={handleSaveAdminUser}>
        Save User
      </button>
    </AdminFormCard>
  );

  if (showLegacyHeader) {
    return (
      <div className="intel-screen">
        <AdminLegacyHeader title="ADMIN USER DETAILS" onBack={onBack} backLabel="User Management" />
        {content}
      </div>
    );
  }

  return content;
}
